import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AES256 } from '@awesome-cordova-plugins/aes-256/ngx';
import { Capacitor } from '@capacitor/core';
import * as CryptoJS from 'crypto-js';
import * as moment from 'moment';
import { ApiService } from 'src/app/services/api.service';
import { environment } from 'src/environments/environment';
import { ApiActivationInterface } from '../interfaces/apiActivation.interface';
import { BDOAESDecryptionResults } from '../interfaces/bdo-aes-decryption-results.interface';
import { BDOAESEncryptionResults } from '../interfaces/bdo-aes-encryption-results.interface';
import { BDOTokenInterface } from '../interfaces/bdo-token.interface';
import { MPDInterface } from '../interfaces/mpd.interface';
import { ContractsService } from './contracts.service';
import { PermissionsService } from './permissions.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class BDODataService {
  private readonly AES_IV: string = environment.mobileAesIV;
  private readonly AES_KEY: string = environment.mobileAesKey;

  private _isDataLoaded: boolean = false;
  private _clientID: string = '';
  private _clientSecret: string = '';

  get isDataLoaded(): boolean {
    return this._isDataLoaded;
  }

  get isLocallyLinked(): boolean {
    if (Capacitor.isNativePlatform()) {
      return this._clientID !== '' && this._clientSecret !== '';
    } else {
      return this.contractsService.mainContract?.integracjaLokalna_WWW;
    }
  }

  get isIntegrated(): boolean {
    return this.contractsService.mainContract?.zintegrowany;
  }

  get isIntegrationDoneByBok(): boolean {
    return this.contractsService.mainContract?.integracjaPrzezBOK;
  }

  get isIntegrationSevered(): boolean {
    // BACKEND - TBD
    return false;
  }

  get bdoUUID(): string {
    return this.contractsService.bdoUUID;
  }

  get clientID(): string {
    return this._clientID;
  }

  get clientSecret(): string {
    return this._clientSecret;
  }

  get shortenedClientID(): string | String {
    return this.shortenKey(this._clientID);
  }

  get shortenedClientSecret(): string | String {
    return this.shortenKey(this._clientSecret);
  }

  constructor(
    private http: HttpClient,
    private aesEncryption: AES256,
    private apiService: ApiService,
    private permissionsService: PermissionsService,
    private storageService: StorageService,
    private contractsService: ContractsService
  ) {}

  public async loadBDOData(): Promise<void> {
    await this.loadLocalBDOKeys();
    this._isDataLoaded = true;
  }

  private async loadLocalBDOKeys(): Promise<void> {
    const encryptedClientID = this.storageService.get('clientID');
    const encryptedClientSecret = this.storageService.get('clientSecret');

    if (
      encryptedClientID === '' ||
      encryptedClientSecret === '' ||
      encryptedClientID == null ||
      encryptedClientSecret == null
    ) {
      this._clientID = '';
      this._clientSecret = '';
      return;
    }

    try {
      let decryptionResults;

      if (Capacitor.isNativePlatform()) {
        decryptionResults = await this.decryptUsingNativeAES(
          encryptedClientID,
          encryptedClientSecret
        );
      } else {
        decryptionResults = this.decryptUsingWebAES(
          encryptedClientID,
          encryptedClientSecret
        );
      }

      this._clientID = decryptionResults.clientID;
      this._clientSecret = decryptionResults.clientSecret;
    } catch (error) {
      this._clientID = '';
      this._clientSecret = '';
      console.log(error);
    }
  }

  private decryptUsingWebAES(
    encryptedClientID: string,
    encryptedClientSecret: string
  ): BDOAESDecryptionResults {
    const clientID = CryptoJS.AES.decrypt(encryptedClientID, this.AES_KEY, {
      iv: CryptoJS.enc.Hex.parse(this.AES_IV),
    }).toString(CryptoJS.enc.Utf8);

    const clientSecret = CryptoJS.AES.decrypt(
      encryptedClientSecret,
      this.AES_KEY,
      {
        iv: CryptoJS.enc.Hex.parse(this.AES_IV),
      }
    ).toString(CryptoJS.enc.Utf8);

    return { clientID, clientSecret };
  }

  private async decryptUsingNativeAES(
    encryptedClientID: string,
    encryptedClientSecret: string
  ): Promise<BDOAESDecryptionResults> {
    const clientID = await this.aesEncryption.decrypt(
      this.AES_KEY,
      this.AES_IV,
      encryptedClientID
    );

    const clientSecret = await this.aesEncryption.decrypt(
      this.AES_KEY,
      this.AES_IV,
      encryptedClientSecret
    );

    return { clientID, clientSecret };
  }

  public async saveAcceptedBDOKeys(
    clientID: string,
    clientSecret: string
  ): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      const encryptionResults = await this.encryptUsingNativeAES(
        clientID,
        clientSecret
      );

      this.storageService.set('clientID', encryptionResults.encryptedClientID);
      this.storageService.set(
        'clientSecret',
        encryptionResults.encryptedClientSecret
      );
    }

    this._clientID = clientID;
    this._clientSecret = clientSecret;
  }

  private async encryptUsingNativeAES(
    clientID: string,
    clientSecret: string
  ): Promise<BDOAESEncryptionResults> {
    const encryptedClientID = await this.aesEncryption.encrypt(
      this.AES_KEY,
      this.AES_IV,
      clientID
    );
    const encryptedClientSecret = await this.aesEncryption.encrypt(
      this.AES_KEY,
      this.AES_IV,
      clientSecret
    );

    return { encryptedClientID, encryptedClientSecret };
  }

  public clearLocalBDOKeys(): void {
    this.storageService.set('clientID', '');
    this.storageService.set('clientSecret', '');
    this._clientID = '';
    this._clientSecret = '';
  }

  public async testBDOAvailability(): Promise<any> {
    if (this.isIntegrated) {
      return;
    }
    return this.testBDOKeys(this._clientID, this._clientSecret);
  }

  public async testBDOKeys(
    clientID: string,
    clientSecret: string
  ): Promise<any> {
    if (!this.permissionsService.hasRestrictedAccess) {
      return this.testClientBDOKeys(clientID, clientSecret);
    } else {
      return this.testRestrictedAccessBDOKeys(clientID, clientSecret);
    }
  }

  private async testClientBDOKeys(
    clientID: string,
    clientSecret: string
  ): Promise<any> {
    const token = this.storageService.get('token');
    const lang = this.storageService.getPersistent('lang');
    const api = this.apiService.api;
    const activationData: ApiActivationInterface =
      this.storageService.get('activationData');
    const data = {
      clientID: clientID,
      clientSecret: clientSecret,
      bdO_UUID: this.bdoUUID,
      nip: activationData?.nip,
      umowaUUID: this.contractsService.mainContract.umowaUUID,
    };

    return this.http
      .request('post', api + '/api/BDO/TestujKlucze', {
        body: data,
        params: {
          Jezyk: lang,
        },
        headers: {
          Token: token,
        },
      })
      .toPromise();
  }

  private async testRestrictedAccessBDOKeys(
    clientID: string,
    clientSecret: string
  ): Promise<any> {
    const token = this.storageService.get('token');
    const lang = this.storageService.getPersistent('lang');
    const api = this.apiService.api;
    const data = {
      clientID: clientID,
      clientSecret: clientSecret,
      bdO_UUID: this.bdoUUID,
      nip: this.storageService.get('NIP'),
    };

    return this.http
      .request('post', api + '/api/Zewnetrzny/BDO/TestujKlucze', {
        body: data,
        params: {
          Jezyk: lang,
        },
        headers: {
          Token: token,
        },
      })
      .toPromise();
  }

  public async generateBDOTokens(
    clientID: string,
    clientSecret: string
  ): Promise<void> {
    const bdO_MPD: MPDInterface[] = this.storageService.get('bdO_MPD');

    for (let mpdIndex = 0; mpdIndex < bdO_MPD.length; ++mpdIndex) {
      const mpd = bdO_MPD[mpdIndex];
      const tokenResponse = await this.requestBDOToken(
        clientID,
        clientSecret,
        mpd.mpD_UUID
      );
      bdO_MPD[mpdIndex].tokenBDO = tokenResponse as BDOTokenInterface;
    }

    this.storageService.set('bdO_MPD', bdO_MPD);
  }

  private async requestBDOToken(
    clientID: string,
    clientSecret: string,
    mpdUUID: string
  ): Promise<BDOTokenInterface> {
    const token = this.storageService.get('token');
    const lang = this.storageService.getPersistent('lang');
    const api = this.apiService.api;

    const data = {
      token: token,
      basicAuth: btoa(
        JSON.stringify({
          MPD_UUID: mpdUUID,
          ClientId: clientID,
          ClientSecret: clientSecret,
        })
      ),
    };

    return await this.http
      .request<BDOTokenInterface>(
        'post',
        api + '/api/Zewnetrzny/BDO/GenerujToken',
        {
          body: data,
          params: {
            Jezyk: lang,
          },
        }
      )
      .toPromise();
  }

  public async getBDOTokenForMPDUUID(mpdUUID: string): Promise<string> {
    const bdO_MPD: MPDInterface[] = this.storageService.get('bdO_MPD');
    const mpdIndex = bdO_MPD.findIndex((mpd) => mpd.mpD_UUID === mpdUUID);
    if (mpdIndex === -1) {
      return;
    }

    await this.checkRefreshBDOToken(bdO_MPD, mpdIndex);
    return bdO_MPD[mpdIndex].tokenBDO.token;
  }

  public async checkRefreshBDOToken(
    bdO_MPD: MPDInterface[],
    mpdIndex: number
  ): Promise<void> {
    const mpd = bdO_MPD[mpdIndex];

    if (
      moment(mpd.tokenBDO.bdO_TokenDostepowy_DataWaznosci).diff(moment()) < 0
    ) {
      const newToken = await this.requestBDOToken(
        this.clientID,
        this.clientSecret,
        mpd.mpD_UUID
      );
      bdO_MPD[mpdIndex].tokenBDO = newToken;
    }
  }

  public async integrateBDO(
    clientID: string,
    clientSecret: string
  ): Promise<any> {
    const token = this.storageService.get('token');
    const lang = this.storageService.getPersistent('lang');
    const api = this.apiService.api;
    const activationData: ApiActivationInterface =
      this.storageService.get('activationData');
    const data = {
      clientID: clientID,
      clientSecret: clientSecret,
      bdO_UUID: this.bdoUUID,
      nip: activationData?.nip,
      umowaUUID: this.contractsService.mainContract.umowaUUID,
    };

    return this.http
      .request('post', api + '/api/BDO/Integruj', {
        body: data,
        params: {
          Jezyk: lang,
        },
        headers: {
          Token: token,
        },
      })
      .toPromise();
  }

  public async locallyLinkWebBdo(
    clientID: string,
    clientSecret: string
  ): Promise<any> {
    const token = this.storageService.get('token');
    const lang = this.storageService.getPersistent('lang');
    const api = this.apiService.api;
    const activationData: ApiActivationInterface =
      this.storageService.get('activationData');
    const data = {
      clientID: clientID,
      clientSecret: clientSecret,
      bdO_UUID: this.bdoUUID,
      nip: activationData?.nip,
      umowaUUID: this.contractsService.mainContract.umowaUUID,
    };

    return this.http
      .request('post', api + '/api/BDO/Integruj_Lokalnie', {
        body: data,
        params: {
          Jezyk: lang,
        },
        headers: {
          Token: token,
        },
      })
      .toPromise();
  }

  public async canBDOTokenBeMade(): Promise<boolean> {
    if (!this.isDataLoaded) {
      await this.loadBDOData();
    }

    if (this.isIntegrated || this.isLocallyLinked) {
      return true;
    } else {
      return false;
    }
  }

  private shortenKey(key: String) {
    if (key.length <= 8) {
      return key;
    }

    let new_key = key.substr(0, 4) + '(...)' + key.substr(key.length - 4, 4);
    return new_key;
  }
}
